import { useState, useCallback, useRef, useEffect } from "react";
import { api } from "@/lib/api";

// P1-B keyset cursor pagination. Appends pages via "Mehr laden" without dropping
// filters/sort/selection, de-dupes by id, and keeps loaded data on error.
export function useCursorList(endpoint, dataKey, params = {}, { limit = 50, enabled = true } = {}) {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const reqId = useRef(0);
  const paramsKey = JSON.stringify(params);

  const fetchPage = useCallback(async (cur) => {
    const my = ++reqId.current;
    const p = { ...JSON.parse(paramsKey), limit };
    if (cur) p.cursor = cur;
    const { data } = await api.get(endpoint, { params: p });
    if (my !== reqId.current) return null; // a newer request superseded this one
    return data;
  }, [endpoint, paramsKey, limit]);

  const reload = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const data = await fetchPage(null);
      if (data === null) return;
      setItems(data[dataKey] || []);
      setCursor(data.next_cursor || null);
      setHasMore(!!data.has_more);
    } catch {
      setError(true); setItems([]); setCursor(null); setHasMore(false);
    } finally { setLoading(false); }
  }, [fetchPage, dataKey]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true); setError(false);
    try {
      const data = await fetchPage(cursor);
      if (data === null) return;
      setItems((prev) => {
        const ids = new Set(prev.map((x) => x.id));
        return [...prev, ...(data[dataKey] || []).filter((x) => !ids.has(x.id))];
      });
      setCursor(data.next_cursor || null);
      setHasMore(!!data.has_more);
    } catch {
      setError(true); // keep already-loaded rows
    } finally { setLoadingMore(false); }
  }, [cursor, loadingMore, fetchPage, dataKey]);

  useEffect(() => { if (enabled) reload(); }, [enabled, reload]);

  return { items, setItems, loading, loadingMore, error, hasMore, loadMore, reload };
}
